import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Typography,
  Grid,
  Box,
} from "@mui/material";
import OfflinePinIcon from "@mui/icons-material/OfflinePin";
import useAxiosPrivate from "../../common/useAxiosPrivate";
import { toast } from "react-toastify";
import { Checklist } from "../CustomComponents/ChecklistInterface";
import MultiSubheaderTableRowComponent from "../CustomComponents/SubheaderTableRowComponent";
import googleDriveAxios from "../../common/googleDriveAxios";

const FinalElectricalStationChecklist: React.FC<{
  chassisNo: string;
  setModel: React.Dispatch<React.SetStateAction<string>>;
  model: string;
  setCheckedBy: React.Dispatch<React.SetStateAction<string>>;
  isDisabled?: boolean;
}> = ({ chassisNo, setModel, model, setCheckedBy, isDisabled }) => {
  const axiosPrivate = useAxiosPrivate();
  const [loading, setLoading] = useState<boolean>(false);
  const [
    externalElectricalTestInspection,
    setExternalElectricalTestInspection,
  ] = useState<Checklist[]>([]);
  const [
    internalElectricalTestInspection,
    setInternalElectricalTestInspection,
  ] = useState<Checklist[]>([]);
  const [appliance, setAppliance] = useState<Checklist[]>([]);
  const [batteryManagementSystem, setBatteryManagementSystem] = useState<
    Checklist[]
  >([]);
  const [batteryManagementSystemFull, setBatteryManagementSystemFull] =
    useState<Checklist[]>([]);
  const [inverter, setInverter] = useState<Checklist[]>([]);
  const [inverterFull, setInverterFull] = useState<Checklist[]>([]);
  const [brakeCheck, setBrakeCheck] = useState<Checklist[]>([]);
  const [operationCheck, setOperationCheck] = useState<Checklist[]>([]);
  const [other, setOther] = useState<Checklist[]>([]);
  const [checkedBy, setLocalCheckedBy] = useState<string>("");
  const [date, setDate] = useState<string>("");
  const [apiError, setApiError] = useState<boolean>(false);
  const [isRadioChanged, setIsRadioChanged] = useState<boolean>(false);
  const [completeStatus, setCompleteStatus] = useState<string>("Incomplete");
  const hasUnsavedChanges = chassisNo;

  const [activeCheckpointList, setActiveCheckpointList] = useState<any[]>([]);
  const checkCheckpointRecord = React.useCallback(async () => {
    if (chassisNo) {
      try {
        const queryParams = new URLSearchParams({
          chassis_no: "DC" + chassisNo,
          dept_name: "final-electrical-inspection",
        }).toString();

        const response = await googleDriveAxios.post(
          `/checkpoint-image-record?${queryParams}`
        );
        const data = response.data;
        setActiveCheckpointList(data.recorded_checkpoint_list);
        console.log(data.recorded_checkpoint_list);
      } catch (error) {
        console.error("Error checking images:", error);
      }
    }
  }, [chassisNo]);

  useEffect(() => {
    checkCheckpointRecord();
  }, [checkCheckpointRecord]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  useEffect(() => {
    const fetchData = async () => {
      if (!chassisNo || chassisNo.length < 4 || chassisNo.length > 5) {
        setExternalElectricalTestInspection([]);
        setInternalElectricalTestInspection([]);
        setAppliance([]);
        setBatteryManagementSystem([]);
        setBatteryManagementSystemFull([]);
        setInverter([]);
        setInverterFull([]);
        setBrakeCheck([]);
        setOperationCheck([]);
        setOther([]);
        setApiError(false);
        setModel("");
        setCompleteStatus("Incomplete");
        setIsRadioChanged(false);
        return;
      }

      setLoading(true);
      setApiError(false);
      setModel("");
      try {
        const response = await axiosPrivate.post(
          `/prepare_final_electrical_inspection/${chassisNo}`
        );
        const data = response.data;

        const externalData = (data.externalElectricalTestInspection || []).map(
          (item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          })
        );
        const internalData = (data.internalElectricalTestInspection || []).map(
          (item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          })
        );
        const applianceData = (data.appliance || []).map((item: Checklist) => ({
          ...item,
          status: item.status
            ? item.status.trim().toLowerCase()
            : "not checked",
        }));
        const batteryData = (data.batteryManagementSystem || []).map(
          (item: Checklist) => {
            return {
              ...item,
              status: item.status
                ? item.status.trim().toLowerCase()
                : "not checked",
            };
          }
        );
        const inverterData = (data.inverter || []).map((item: Checklist) => ({
          ...item,
          status: item.status
            ? item.status.trim().toLowerCase()
            : "not checked",
        }));
        const brakeData = (data.brakeCheck || []).map((item: Checklist) => ({
          ...item,
          status: item.status
            ? item.status.trim().toLowerCase()
            : "not checked",
        }));
        const operationData = (data.operationCheck || []).map(
          (item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          })
        );
        const otherData = (data.other || []).map((item: Checklist) => ({
          ...item,
          status: item.status
            ? item.status.trim().toLowerCase()
            : "not checked",
        }));

        setExternalElectricalTestInspection(externalData);
        setInternalElectricalTestInspection(internalData);
        setAppliance(applianceData);
        setBatteryManagementSystemFull(batteryData);
        setInverterFull(inverterData);
        setBrakeCheck(brakeData);
        setOperationCheck(operationData);
        setOther(otherData);
        setLocalCheckedBy(data.checkedBy || "");
        setCheckedBy(data.checkedBy || "");
        setModel(data.model || "");

        const bmsBrandRow = batteryData.find(
          (item: { title: string }) => item.title === "BMS Brand"
        );
        if (
          bmsBrandRow &&
          (!bmsBrandRow.status || bmsBrandRow.status === "not checked")
        ) {
          setBatteryManagementSystem([bmsBrandRow]);
        } else {
          setBatteryManagementSystem(batteryData);
        }

        const inverterBrandRow = inverterData.find(
          (item: { title: string }) => item.title === "Inverter Brand"
        );
        if (
          inverterBrandRow &&
          (!inverterBrandRow.status ||
            inverterBrandRow.status === "not checked")
        ) {
          setInverter([inverterBrandRow]);
        } else {
          setInverter(inverterData);
        }

        let formattedDate = "";
        if (data.createdOn) {
          const date = new Date(data.createdOn);
          if (date.getFullYear() !== 1900) {
            formattedDate = data.createdOn ? data.createdOn.split("T")[0] : "";
          }
        }
        setDate(formattedDate);

        const allItems = [
          ...externalData,
          ...internalData,
          ...applianceData,
          ...batteryData.filter(
            (item: { title: string }) => item.title !== "BMS Brand"
          ),
          ...inverterData.filter(
            (item: { title: string }) => item.title !== "Inverter Brand"
          ),
          ...brakeData,
          ...operationData,
          ...otherData,
        ];
        const isComplete =
          allItems.length > 0 &&
          allItems
            .filter(
              (item) =>
                item.title !== "BMS Brand" && item.title !== "Inverter Brand"
            )
            .every(
              (item) =>
                item.status &&
                ["pass", "fail", "n/a"].includes(item.status.toLowerCase())
            );

        setCompleteStatus(isComplete ? "Complete" : "Incomplete");
        setIsRadioChanged(false);
      } catch (error: any) {
        if (error.response?.status === 500) {
          setApiError(true);
        } else {
          toast.error("Failed to fetch data.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [chassisNo, setModel, axiosPrivate, setCheckedBy]);

  const saveData = async () => {
    if (!date.trim()) {
      toast.error("Date is required.");
      return;
    }
    if (!checkedBy.trim()) {
      toast.error("Checked By field is required.");
      return;
    }

    const bmsBrandRow = batteryManagementSystem.find(
      (item) => item.title === "BMS Brand"
    );
    if (!bmsBrandRow || bmsBrandRow.status === "not checked") {
      toast.error("BMS Brand must be selected.");
      return;
    }

    const inverterBrandRow = inverter.find(
      (item) => item.title === "Inverter Brand"
    );
    if (!inverterBrandRow || inverterBrandRow.status === "not checked") {
      toast.error("Inverter Brand must be selected.");
      return;
    }

    setLoading(true);
    try {
      const allChecklists = [
        ...externalElectricalTestInspection,
        ...internalElectricalTestInspection,
        ...appliance,
        ...batteryManagementSystem.filter(
          (item) => item.title !== "BMS Brand"
        ),
        ...inverter.filter((item) => item.title !== "Inverter Brand"),
        ...brakeCheck,
        ...operationCheck,
        ...other,
      ];

      if (isRadioChanged) {
        const isComplete =
          allChecklists.length > 0 &&
          allChecklists.every(
            (item) =>
              item.status &&
              ["pass", "fail", "n/a"].includes(item.status.toLowerCase())
          );
        setCompleteStatus(isComplete ? "Complete" : "Incomplete");
      }

      const payload = {
        chassisNo,
        model: model,
        externalElectricalTestInspection: externalElectricalTestInspection.map(
          (item) => ({
            title: item.title,
            notes: item.notes,
            status: item.status,
          })
        ),
        internalElectricalTestInspection: internalElectricalTestInspection.map(
          (item) => ({
            title: item.title,
            notes: item.notes,
            status: item.status,
          })
        ),
        appliance: appliance.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        batteryManagementSystem: batteryManagementSystem.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        inverter: inverter.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        brakeCheck: brakeCheck.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        operationCheck: operationCheck.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        other: other.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        checkedBy,
        createdOn: date,
        completeStatus,
      };

      await toast.promise(
        axiosPrivate.post(
          "/create_or_update_final_electrical_inspection",
          payload
        ),
        {
          pending: "Saving data...",
          success: "Data saved successfully!",
          error: "Failed to save data.",
        }
      );

      setIsRadioChanged(false);

      if (checkedBy) {
        const allChecklists = [
          ...externalElectricalTestInspection,
          ...internalElectricalTestInspection,
          ...appliance,
          ...batteryManagementSystem.filter(
            (item) => item.title !== "BMS Brand"
          ),
          ...inverter.filter((item) => item.title !== "Inverter Brand"),
          ...brakeCheck,
          ...operationCheck,
          ...other,
        ];

        let itemNoCounter = 1;
        const newReworkItems = allChecklists
          .filter(
            (item) =>
              item.status?.toLowerCase() === "fail" &&
              item.title?.toLowerCase() !== "inverter brand"
          )
          .flatMap((item) => {
            const title = item.title || "No Title Available";
            const notesStr = item.notes || "";

            if (notesStr.includes(";")) {
              const parts = notesStr
                .split(";")
                .map((part) => part.trim())
                .filter((part) => part);
              return parts.map((note) => ({
                itemNo: itemNoCounter++,
                problemDescription: `${title}: ${note}`,
                comment: "",
                whoFoundFault: checkedBy,
                responsible: "",
                whoDidRework: "",
                timeSpentOnRework: { hours: 0, minutes: 0, seconds: 0 },
                completed: false,
              }));
            } else {
              return [
                {
                  itemNo: itemNoCounter++,
                  problemDescription: `${title}: ${notesStr}`,
                  comment: "",
                  whoFoundFault: checkedBy,
                  responsible: "",
                  whoDidRework: "",
                  timeSpentOnRework: { hours: 0, minutes: 0, seconds: 0 },
                  completed: false,
                },
              ];
            }
          });

        if (newReworkItems.length > 0) {
          const reworkPayload = {
            chassisNo,
            model,
            station_name: "final-electrical-inspection",
            reworkItems: newReworkItems,
          };

          await axiosPrivate.post(`rework/save`, reworkPayload, {
            params: { rework_type: "final-electrical-inspection" },
          });
        }
      }
    } catch (error) {
      console.error("Error saving data:", error);
      toast.error("Failed to save data.");
    } finally {
      setLoading(false);
    }
  };

  const handleNotesChange = (index: number, value: string, type: string) => {
    if (completeStatus === "Complete") return;

    const updateChecklist = (
      checklist: Checklist[],
      setChecklist: React.Dispatch<React.SetStateAction<Checklist[]>>
    ) => {
      const updatedData = [...checklist];
      updatedData[index].notes = value;
      setChecklist(updatedData);
    };

    if (type === "external_electrical_test_inspection") {
      updateChecklist(
        externalElectricalTestInspection,
        setExternalElectricalTestInspection
      );
    }
    if (type === "internal_electrical_test_inspection") {
      updateChecklist(
        internalElectricalTestInspection,
        setInternalElectricalTestInspection
      );
    }
    if (type === "appliance") {
      updateChecklist(appliance, setAppliance);
    }
    if (type === "battery_management_system") {
      updateChecklist(batteryManagementSystem, setBatteryManagementSystem);
    }
    if (type === "inverter") {
      updateChecklist(inverter, setInverter);
    }
    if (type === "brake_check") {
      updateChecklist(brakeCheck, setBrakeCheck);
    }
    if (type === "operational_check") {
      updateChecklist(operationCheck, setOperationCheck);
    }
    if (type === "other") {
      updateChecklist(other, setOther);
    }
  };

  const handleRadioChange = (index: number, value: string, type: string) => {
    if (completeStatus === "Complete") return;

    setIsRadioChanged(true);

    const updateChecklist = (
      checklist: Checklist[],
      setChecklist: React.Dispatch<React.SetStateAction<Checklist[]>>,
      type: string
    ) => {
      const updatedData = [...checklist];
      updatedData[index].status = value;

      if (
        type === "battery_management_system" &&
        updatedData[index].title === "BMS Brand"
      ) {
        const selectedBrand = value.toLowerCase();
        console.log("Selected BMS Brand:", selectedBrand);
        console.log(
          "batteryManagementSystemFull:",
          batteryManagementSystemFull
        );
        console.log("inverter (before BMS update):", inverter); // Debug inverter state

        const brandPrefixes: Record<string, string[]> = {
          enerdrive: ["ENERDRIVE :"],
          projecta: ["PROJECTA :"],
          victron: ["VICTRON :"],
          redarc: ["REDARC :"],
          bp35: ["BP35 :"],
          aspero: ["ASPERO :"],
        };

        const commonTitles = [
          "SHUNT",
          "WIRELESS LIGHT REMOTE",
          "WATER LEVEL GAUGES",
          "TRAIL SAFE :",
        ];

        if (!brandPrefixes[selectedBrand]) {
          console.log("No valid BMS brand selected, keeping BMS Brand row");
          setBatteryManagementSystem([updatedData[index]]);
          setChecklist([updatedData[index]]);
          return;
        }

        const filtered = batteryManagementSystemFull.filter((item) => {
          if (item.title === "BMS Brand") return true;
          const prefixes = brandPrefixes[selectedBrand];
          return (
            prefixes.some((prefix) => item.title?.startsWith(prefix)) ||
            (selectedBrand &&
              commonTitles.some((title) => item.title?.startsWith(title)))
          );
        });

        console.log(`Filtered BMS ${selectedBrand} items:`, filtered);
        setBatteryManagementSystem(filtered);
        setChecklist(filtered);
      } else if (
        type === "inverter" &&
        updatedData[index].title === "Inverter Brand"
      ) {
        const selectedInverterBrand = value.toLowerCase();
        console.log(`Selected Inverter Brand: ${selectedInverterBrand}`);
        console.log("inverterFull:", inverterFull);

        const brandPrefixes: Record<string, string> = {
          enerdrive: "ENERDRIVE",
          victron: "VICTRON",
          projecta: "PROJECTA",
        };

        if (!brandPrefixes[selectedInverterBrand]) {
          console.log(
            "No valid Inverter brand selected, keeping Inverter Brand row"
          );
          setInverter([updatedData[index]]);
          setChecklist([updatedData[index]]);
          return;
        }

        const filtered = inverterFull.filter((item) => {
          if (item.title === "Inverter Brand") return true;
          const prefix = brandPrefixes[selectedInverterBrand];
          return item.title?.startsWith(prefix);
        });

        console.log(
          `Filtered Inverter ${selectedInverterBrand} items:`,
          filtered
        );
        setInverter(filtered);
        setChecklist(filtered);
      } else {
        setChecklist(updatedData);
      }
    };

    switch (type) {
      case "external_electrical_test_inspection":
        updateChecklist(
          externalElectricalTestInspection,
          setExternalElectricalTestInspection,
          type
        );
        break;
      case "internal_electrical_test_inspection":
        updateChecklist(
          internalElectricalTestInspection,
          setInternalElectricalTestInspection,
          type
        );
        break;
      case "appliance":
        updateChecklist(appliance, setAppliance, type);
        break;
      case "battery_management_system":
        updateChecklist(
          batteryManagementSystem,
          setBatteryManagementSystem,
          type
        );
        break;
      case "inverter":
        updateChecklist(inverter, setInverter, type);
        break;
      case "brake_check":
        updateChecklist(brakeCheck, setBrakeCheck, type);
        break;
      case "operational_check":
        updateChecklist(operationCheck, setOperationCheck, type);
        break;
      case "other":
        updateChecklist(other, setOther, type);
        break;
      default:
        console.warn("Unknown checklist type:", type);
    }
  };

  const allChecklists = [
    ...externalElectricalTestInspection,
    ...internalElectricalTestInspection,
    ...appliance,
    ...batteryManagementSystem.filter((item) => item.title !== "BMS Brand"),
    ...inverter.filter((item) => item.title !== "Inverter Brand"),
    ...brakeCheck,
    ...operationCheck,
    ...other,
  ];

  const allNotChecked =
    allChecklists.length > 0 &&
    allChecklists.every((item) => item.status?.toLowerCase() === "not checked");

  const checklistsWithSerialNumbers = [
    {
      data: externalElectricalTestInspection,
      type: "external_electrical_test_inspection",
      subHeaderText: "External - Electrical Test Inspection",
    },
    {
      data: internalElectricalTestInspection,
      type: "internal_electrical_test_inspection",
      subHeaderText: "Internal - Electrical Test Inspection",
    },
    {
      data: appliance,
      type: "appliance",
      subHeaderText: "Appliance",
    },
    {
      data: batteryManagementSystem,
      type: "battery_management_system",
      subHeaderText: "Battery Management System / Programming",
    },
    {
      data: inverter,
      type: "inverter",
      subHeaderText: "Inverter",
    },
    {
      data: brakeCheck,
      type: "brake_check",
      subHeaderText: "Brake Check",
    },
    {
      data: operationCheck,
      type: "operational_check",
      subHeaderText: "Operational Check",
    },
    {
      data: other,
      type: "other",
      subHeaderText: "Other",
    },
  ];

  let serialNumberCounter = 0;

  return (
    <Box
      sx={{
        position: "relative",
        filter: loading ? "blur(5px)" : "none",
        pointerEvents: loading ? "none" : "auto",
      }}
    >
      <Typography
        sx={{
          mb: 2,
          fontWeight: "600",
          display: "flex",
          alignItems: "center",
          gap: "5px",
          color:
            completeStatus === "Incomplete" ? "error.main" : "success.main",
        }}
      >
        {completeStatus === "Incomplete" ? (
          <>⚠️ INCOMPLETE</>
        ) : (
          <>
            <OfflinePinIcon /> COMPLETE
          </>
        )}
      </Typography>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow className="table__head_row">
              <TableCell
                className="table__head_cell"
                sx={{
                  width: "1%",
                  textAlign: "center",
                  verticalAlign: "middle",
                  padding: "0px",
                }}
              ></TableCell>
              <TableCell
                className="table__head_cell"
                sx={{
                  width: "1%",
                  textAlign: "center",
                  verticalAlign: "middle",
                  padding: "0px",
                }}
              >
                S/N
              </TableCell>
              <TableCell className="table__head_cell" sx={{ width: "15%" }}>
                CHECKLIST
              </TableCell>
              <TableCell className="table__head_cell" sx={{ width: "15%" }}>
                NOTES
              </TableCell>
              <TableCell
                className="table__head_cell"
                sx={{ width: "5%" }}
                align="center"
              >
                STATUS
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Box
                    sx={{
                      position: "absolute",
                      top: "60%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      zIndex: 10,
                    }}
                  >
                    <Typography>Loading...</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : apiError ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="error">Invalid DC Number</Typography>
                </TableCell>
              </TableRow>
            ) : (
              <React.Fragment>
                {checklistsWithSerialNumbers.map((checklistSection) => {
                  const serialNumberStart = serialNumberCounter + 1;
                  serialNumberCounter += checklistSection.data.length;

                  console.log(
                    `Rendering ${checklistSection.type} with serialNumberStart: ${serialNumberStart}, item count: ${checklistSection.data.length}`
                  );

                  return (
                    <MultiSubheaderTableRowComponent
                      key={checklistSection.type}
                      subHeaderText={checklistSection.subHeaderText}
                      type={checklistSection.type}
                      data={checklistSection.data}
                      handleNotesChange={handleNotesChange}
                      handleRadioChange={handleRadioChange}
                      disabled={completeStatus === "Complete"}
                      allNotChecked={allNotChecked}
                      serialNumberStart={serialNumberStart}
                      chassisDCNumber={"DC" + chassisNo}
                      deptName={"final-electrical-inspection"}
                      activeCheckpointList={activeCheckpointList}
                      checkCheckpointRecord={checkCheckpointRecord}
                    />
                  );
                })}
              </React.Fragment>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Grid container spacing={3} my={4}>
        <Grid item md={4}>
          <TextField
            label="Checked By"
            value={checkedBy}
            onChange={(e) => {
              if (completeStatus === "Complete") return;
              setLocalCheckedBy(e.target.value);
              setCheckedBy(e.target.value);
            }}
            fullWidth
            hidden={apiError}
            disabled={completeStatus === "Complete"}
          />
        </Grid>
        <Grid item md={4}>
          <TextField
            label="Date"
            type="date"
            value={date}
            onChange={(e) => {
              if (completeStatus === "Complete") return;
              setDate(e.target.value);
            }}
            fullWidth
            InputLabelProps={{ shrink: true }}
            hidden={apiError}
            disabled={completeStatus === "Complete"}
          />
        </Grid>
      </Grid>
      <Button
        variant="contained"
        color="primary"
        onClick={saveData}
        disabled={
          allChecklists.length === 0 ||
          loading ||
          (!chassisNo && !model) ||
          completeStatus === "Complete"
        }
      >
        Save
      </Button>
    </Box>
  );
};

export default FinalElectricalStationChecklist;
